import { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import { useSnackbar } from 'notistack';
import { ApiServices } from "../../utils/ApiService";
import { exportDashboardCSV } from "../../export/exportDashboardCSV";
import DashboardBranchBarChart from "../../components/DashboardBranchBarChart.tsx";

const tilesMeta = [
  { key: "loginFiles", label: "Login", bgColor: "#1565c0" },
  { key: "notAssignedFiles", label: "Not Assigned", bgColor: "#2e7d32" },
  { key: "completedFiles", label: "Completed", bgColor: "#00897b" },
  { key: "lastMonthLoginFiles", label: "Last Month Login", bgColor: "#c62828" },
  { key: "lastMonthCompletedFiles", label: "Last Month Completed", bgColor: "#ef6c00" },
  { key: "assignedFiles", label: "Assigned", bgColor: "#d84315" },
  { key: "queryNotRaisedFiles", label: "Query Not Raised", bgColor: "#6a1b9a" },
];

export default function AdminDashboardPage() {
  const [range, setRange] = useState("FTD");
  const [dashboardData, setDashboardData] = useState({
    FTD: {},
    MTD: {},
    YTD: {},
  });
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const userId = JSON.parse(localStorage.getItem("user"))?.id;

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const isSuperAdmin = userData?.role === "Super Admin";
  // ✅ New state for branch
  const [branch, setBranch] = useState(""); // default branch

  const [loginFileCount, setLoginFileCount] = useState();

  useEffect(() => {
    const fetchLoginFilesCount = async () => {
      try {
        // ✅ Hit branch API when branch is selected
        let res = await ApiServices.get(`file/get-file-login-counts`);

        if (res?.status === 200) {
          setLoginFileCount(res?.data);
        } else {
          enqueueSnackbar("Failed to load metrics.", { variant: "error" });
        }
      } catch (error) {
        console.error("Fetch error:", error);
        enqueueSnackbar("An error occurred while fetching data.", { variant: "error" });
      }
    };

    fetchLoginFilesCount();
  }, [userId]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        let res;
        if (branch) {
          // ✅ Hit branch API when branch is selected
          res = await ApiServices.get(`file/admin-dashboard/branch/${branch}`);
        } else {
          // ✅ Default by userId
          res = await ApiServices.get(`file/admin-dashboard/${userId}`);
        }

        if (res?.status === 200) {
          setDashboardData(res?.data);
        } else {
          enqueueSnackbar("Failed to load metrics.", { variant: "error" });
        }
      } catch (error) {
        console.error("Fetch error:", error);
        enqueueSnackbar("An error occurred while fetching data.", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userId, branch]); // ✅ refetch when branch changes

  const groupedTiles = (tiles, groupSize) =>
    tiles.reduce((acc, _, i) => {
      if (i % groupSize === 0) acc.push(tiles.slice(i, i + groupSize));
      return acc;
    }, []);

  const rows = groupedTiles(tilesMeta, 4);
  const counts = dashboardData[range] || {}; // ⬅️ use selected range data

  return (
    <Box sx={{ padding: 3 }}>
      <Box position="relative" display="flex" justifyContent="flex-end" alignItems="center" mb={0}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "#131d38",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          Yaazh Legal Associates
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={() => exportDashboardCSV(dashboardData)}
        >
          Export
        </Button>
      </Box>

      {/* 📅 Range Selector */}
      <Box display="flex" justifyContent="center" mb={2}>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(e, newRange) => {
            if (newRange !== null) setRange(newRange);
          }}
          color="primary"
          aria-label="range selection"
          sx={{
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1.5,
              fontWeight: "bold",
              fontSize: "1rem",
              borderRadius: 2,
              textTransform: "none",
              color: (theme) => theme.palette.primary.main,   // ← uses theme
            },
            "& .Mui-selected": {
              backgroundColor: "#26c6da",   // ← theme color
              color: "#fff",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.dark, // ← darker variant
              },
            },
          }}
        >
          <ToggleButton value="FTD" aria-label="FTD">
            FTD
          </ToggleButton>
          <ToggleButton value="MTD" aria-label="MTD">
            MTD
          </ToggleButton>
          <ToggleButton value="YTD" aria-label="YTD">
            YTD
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 🏢 Branch Selector - show only for Super Admin */}
      {/* {isSuperAdmin && (
        <Box display="flex" justifyContent="center" mb={1}>
          <RadioGroup
            row
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            <FormControlLabel value="" control={<Radio />} label="Default" />
            <FormControlLabel value="TEN" control={<Radio />} label="TEN" />
            <FormControlLabel value="CBE" control={<Radio />} label="CBE" />
          </RadioGroup>
        </Box>
      )} */}

      {rows?.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {row?.map((tile, tileIndex) => (
            <motion.div
              key={tileIndex}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
              style={{ width: "100%", maxWidth: "200px" }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  backgroundColor: tile.bgColor,
                  color: "#ffffff",
                  borderRadius: 3,
                  textAlign: "center",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {tile.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {counts[tile.key] ?? "--"}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      ))}

      {isSuperAdmin && (
        <Box mt={5} width="100%">
          <Typography variant="h6" align="center" mb={2}>
            Login Files - {range}
          </Typography>
          <DashboardBranchBarChart data={loginFileCount} range={range} />
        </Box>
      )}
    </Box>
  );
}
